import { Controller } from "@hotwired/stimulus";

/**
 * Makes a whole table row open a destination.
 *
 * The row is the affordance, so the list carries no per-row action button. The first
 * cell keeps a real anchor, which is what gives keyboard and screen-reader users the
 * same destination — this controller only widens the hit area for pointer users.
 */
export default class extends Controller<HTMLElement> {
    static values = { url: String };

    declare readonly urlValue: string;

    open(event: MouseEvent): void {
        if (!this.urlValue) {
            return;
        }

        // Let anything genuinely interactive inside the row win: a link, a button, a
        // form control, or a modifier-click the user meant as "open in a new tab".
        const target = event.target;

        if (target instanceof Element && target.closest("a, button, input, select, textarea, label")) {
            return;
        }

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
        }

        const anchor = this.element.querySelector("a[href]");

        if (anchor instanceof HTMLAnchorElement) {
            anchor.click();
            return;
        }

        window.location.assign(this.urlValue);
    }

    /* Swallows a click that belongs to a control sitting inside the row.

    The row opens the lot; a button on it does its own thing. Without this the two fire
    together and the manager lands on a panel they did not ask for. */
    ignore(event: Event): void {
        event.stopPropagation();
    }
}
